import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../../Containers/Common/Modal';
import styles from './Error.module.scss';
import { actions } from './store';
import { getError } from './store/selectors';
import { useErrorTranslations } from './constants/translations';
import useWindowSize from 'utils/useWindowSize';
import clx from 'classnames';
import useOutsideClick from 'utils/useOutsideClick';
import { ROUTES } from 'routes/constants';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { authActions } from '../../Containers/Auth/store';
import { clearSessionStoragePreservingFromApp } from 'utils/clearSessionStorage';
import { sendMessageToApp } from 'utils/exsternalCall';
import { AlertTriangle, MessageCircle, ShieldX, X } from 'lucide-react';

const SUPPORT_WHATSAPP_NUMBER = '393533357748';
const CREDENTIAL_ERRORS = new Set(['mailNotFound', 'unauthorized']);

export default () => {
  const error = useSelector(getError());
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const errorTranslations = useErrorTranslations();
  const isCredentialError = CREDENTIAL_ERRORS.has(error);
  const { isMobile } = useWindowSize();

  React.useEffect(() => {
    if (error !== 'invalidToken') {
      return;
    }
    clearSessionStoragePreservingFromApp();
    dispatch(authActions.LOG_OUT.SUCCESS());
  }, [error, dispatch]);

  const goToLogin = () => {
    dispatch(actions.clearError());
    if (error === 'invalidToken') {
      clearSessionStoragePreservingFromApp();
      dispatch(authActions.LOG_OUT.SUCCESS());
      window.location.href = ROUTES.SIGN_IN;
    }
  };

  const clear = () => {
    dispatch(actions.clearError());
    if (error === 'invalidToken') {
      goToLogin();
    }
  };

  const goToPasswordRecovery = () => {
    dispatch(actions.clearError());
    navigate(ROUTES.RECOVERY);
  };

  const contactSupportWhatsapp = () => {
    const messageText = t('errors.credentialsHelp.whatsappMessage');
    const url = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(messageText)}`;
    if (!localStorage.getItem('fromApp')) window?.open(url, '_blank');
    else sendMessageToApp('app:whatsapp', { number: SUPPORT_WHATSAPP_NUMBER, text: messageText });
  };

  const modalRef = useRef(null);
  useOutsideClick(modalRef, () => {
    clear();
  });

  const stripeCode = error && typeof error === 'object' ? error.code || error.raw?.code : null;
  const stripeMessage =
    error && typeof error === 'object'
      ? String(error.raw?.message || error.message || '')
      : '';
  const isMissingPaymentMethod =
    stripeCode === 'resource_missing' ||
    /no attached payment source|default payment method/i.test(stripeMessage);

  const title =
    error && typeof error === 'object'
      ? isMissingPaymentMethod
        ? 'Completa il pagamento su Stripe'
        : 'Non è stato possibile completare il pagamento'
      : errorTranslations[error]?.title || error;
  const subtitle =
    error && typeof error === 'object'
      ? isMissingPaymentMethod
        ? 'Scegli di nuovo il piano: si apre Stripe per inserire carta o addebito RID e attivare l’abbonamento.'
        : 'Riprova tra qualche istante. Se il problema continua, contatta l’assistenza.'
      : errorTranslations[error]?.subtitle;

  return (
    <Modal
      width={isCredentialError ? '400px' : '360px'}
      height="auto"
      maxWidth="calc(100vw - 32px)"
      isModalOpen={Boolean(error)}
      isError
      centered
      setIsModalOpen={clear}
      borderRadius="16px"
    >
      <div
        ref={modalRef}
        className={clx(styles.panel, {
          [styles.panel_mobile]: isMobile,
          [styles.panel_credentials]: isCredentialError,
        })}
      >
        <button type="button" className={styles.close_btn} onClick={clear} aria-label="Chiudi">
          <X size={18} strokeWidth={2.4} aria-hidden />
        </button>

        {isCredentialError ? (
          <>
            <div className={styles.hero}>
              <div className={styles.icon_wrap} aria-hidden>
                <ShieldX size={22} strokeWidth={2.3} />
              </div>
              <h2 className={styles.title}>{title}</h2>
              {subtitle ? <p className={styles.label}>{subtitle}</p> : null}
            </div>

            <div className={styles.actions}>
              <button type="button" className={styles.btn_primary} onClick={clear}>
                {t('errors.credentialsHelp.retry')}
              </button>
              <button type="button" className={styles.btn_secondary} onClick={goToPasswordRecovery}>
                {t('errors.credentialsHelp.recoverPasswordButton')}
              </button>
            </div>

            <button type="button" className={styles.support_link} onClick={contactSupportWhatsapp}>
              <MessageCircle size={15} strokeWidth={2.3} aria-hidden />
              <span>
                {t('errors.credentialsHelp.forgotEmailPrefix')}{' '}
                <strong>{t('errors.credentialsHelp.forgotEmailLink')}</strong>
              </span>
            </button>
          </>
        ) : (
          <>
            <div className={styles.hero}>
              <div className={styles.icon_wrap} aria-hidden>
                <AlertTriangle size={22} strokeWidth={2.3} />
              </div>
              <h2 className={styles.title}>{title}</h2>
              {subtitle ? <p className={styles.label}>{subtitle}</p> : null}
            </div>
            <div className={styles.actions}>
              {error === 'invalidToken' ? (
                <button type="button" className={styles.btn_primary} onClick={goToLogin}>
                  {t('modals.tokenExpired.buttons.loginAgain')}
                </button>
              ) : (
                <button type="button" className={styles.btn_primary} onClick={clear}>
                  {t('common.actions.close')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
